import React, { FC, UIEvent, useEffect, useRef, useState } from 'react';
import ChatMessage from '../chat-message/chat-message';
import { getDate } from '../../../utils/helpers/date-helper';
import { AVAILABLE_SCROLL_HEIGHT_TO_TOP } from '../../../constans/chat-messages-constants';
import { IChatMessage } from '../../../store/chat-store/chat-store';
import { isElementScrollPositionBottom } from '../../../utils/helpers/html-helpers';
import { ChatNewMessagesIndicator } from '../chat-new-messages-indicator/chat-new-messages-indicator';

interface IProps {
  userName: string;
  messages: IChatMessage[];
  isVisible: boolean;
  isVisibleNewMessageIndicator: boolean;
  clearUserLastMessageData: () => void;
  isMyMessage: () => boolean;
  paginationHandler: () => void;
  defineNewMessageIndicatorVisibilityState: (isChatScrolledToBottom: boolean) => void;
  incrementUnreadMessagesNumber: () => void;
  resetUnreadMessagesNumber: () => void;
}

const ChatMessagesContainer: FC<IProps> = ({
  userName,
  messages,
  isVisible,
  isVisibleNewMessageIndicator,
  clearUserLastMessageData,
  isMyMessage,
  paginationHandler,
  defineNewMessageIndicatorVisibilityState,
  incrementUnreadMessagesNumber,
  resetUnreadMessagesNumber,
}): JSX.Element => {
  const [loadMore, setLoadMore] = useState<boolean>(false);

  const shouldScrollToBottomRef = useRef<boolean>(false);
  const getShouldScrollToBottom = (): boolean => shouldScrollToBottomRef.current;
  const setShouldScrollToBottom = (value: boolean): void => {
    shouldScrollToBottomRef.current = value;
  };

  const chatBoxElRef = useRef<HTMLDivElement>(null);
  const chatBoxEl = chatBoxElRef.current!;

  const handleScroll = (e: UIEvent<HTMLElement>) => {
    const scrollTop = e.currentTarget.scrollTop < AVAILABLE_SCROLL_HEIGHT_TO_TOP;
    scrollTop ? setLoadMore(true) : setLoadMore(false);

    const scrollPositionBottom = isElementScrollPositionBottom(e.currentTarget);

    if (scrollPositionBottom && !getShouldScrollToBottom()) {
      setShouldScrollToBottom(true);
      defineNewMessageIndicatorVisibilityState(true);
      isVisible && resetUnreadMessagesNumber();
    } else if (!scrollPositionBottom && getShouldScrollToBottom()) {
      setShouldScrollToBottom(false);
    }
  };

  const scrollToBottomHandler = (): void => {
    if (!chatBoxEl) return;

    window.requestAnimationFrame(() => {
      chatBoxEl.scrollTop = chatBoxEl.scrollHeight;
      window.requestAnimationFrame(() => setShouldScrollToBottom(true));
    });
    isVisible && resetUnreadMessagesNumber();
    defineNewMessageIndicatorVisibilityState(true);
  };

  const onReceiveNewMessages = () => {
    if (isMyMessage()) {
      scrollToBottomHandler();
      clearUserLastMessageData();
    } else if (getShouldScrollToBottom()) {
      scrollToBottomHandler();
    } else {
      if (!chatBoxEl) return;
      isVisible && incrementUnreadMessagesNumber();
    }

    defineNewMessageIndicatorVisibilityState(getShouldScrollToBottom());
  };

  useEffect(() => {
    onReceiveNewMessages();
  }, [messages]);

  useEffect(() => {
    scrollToBottomHandler();
  }, [isVisible]);

  useEffect(() => {
    if (loadMore) {
      paginationHandler();
    }
  }, [loadMore]);

  return (
    <div className="chat-box-messages" onScroll={handleScroll} ref={chatBoxElRef}>
      {messages.map(item => (
        <ChatMessage
          isMyMessage={item.senderName !== userName}
          key={item.chatMessageId}
          date={getDate(item.createdAt)}
          message={item.message}
          user={item.senderName}
        />
      ))}
      <ChatNewMessagesIndicator
        isVisible={isVisibleNewMessageIndicator}
        onClick={scrollToBottomHandler}
      />
    </div>
  );
};

export default ChatMessagesContainer;
